// SmsConfigCard — single self-contained card for the company's SMS provider.
//
// Q3/Q5 lock from EMAIL_NOTIFICATIONS_PLAN: ClickSend is the only live
// provider in v1; Twilio + Vonage render as disabled "Coming Soon" options.
// Only one active SMS row per tenant — the schema's unique key permits more
// but the UI manages a single record.

import { useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { Input } from '../../../../components/form/Input';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import {
  useSmsSettings,
  useTestSmsConnection,
  useUpdateSmsSettings,
} from '../hooks/useCommunicationSettings';
import type { SmsProvider, SmsSettings, UpsertSmsInput } from '../../../../types/api';
import styles from './SmsConfigCard.module.css';

interface FormState {
  provider: SmsProvider;
  api_username: string;
  api_key: string;
  from_number: string;
}

const EMPTY_FORM: FormState = {
  provider: 'clicksend',
  api_username: '',
  api_key: '',
  from_number: '',
};

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffMs = Date.now() - t;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} hr ago`;
  return `${Math.floor(sec / 86400)} days ago`;
}

function formFromSettings(s: SmsSettings | null): FormState {
  if (!s) return EMPTY_FORM;
  return {
    provider: s.provider,
    api_username: s.api_username ?? '',
    api_key: '',
    from_number: s.from_number ?? '',
  };
}

export function SmsConfigCard(): JSX.Element {
  const query = useSmsSettings();
  const update = useUpdateSmsSettings();
  const test = useTestSmsConnection();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [testOpen, setTestOpen] = useState(false);
  const [testNumber, setTestNumber] = useState('');
  const [testMessage, setTestMessage] = useState<{
    kind: 'success' | 'error';
    text: string;
  } | null>(null);

  useEffect(() => {
    setForm(formFromSettings(query.data ?? null));
  }, [query.data]);

  const sms = query.data ?? null;
  const notConfigured = sms === null || sms.from_number === null;
  const isClickSend = form.provider === 'clicksend';

  function startEdit() {
    setSaveError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setSaveError(null);
    setForm(formFromSettings(sms));
    setEditing(false);
  }

  async function handleSave() {
    setSaveError(null);
    if (!isClickSend) {
      setSaveError('Only ClickSend can be saved at this time.');
      return;
    }
    if (!form.api_username || !form.from_number) {
      setSaveError('Please fill in every field.');
      return;
    }
    // Blank api_key = "keep existing" — server reuses the stored
    // ciphertext. Required only when this is a brand-new config (server
    // returns 400 password_required_for_new_config in that case).
    const body: UpsertSmsInput = {
      provider: 'clicksend',
      api_username: form.api_username,
      api_key: form.api_key.length > 0 ? form.api_key : null,
      from_number: form.from_number,
    };
    await update.mutateAsync(body, {
      onSuccess: () => setEditing(false),
      onError: (err) => setSaveError(extractSaveError(err)),
    });
  }

  async function handleToggleActive(next: boolean) {
    if (!sms) return;
    setSaveError(null);
    // Send null api_key — server keeps the existing ciphertext.
    const body: UpsertSmsInput = {
      provider: sms.provider,
      api_username: sms.api_username,
      api_key: form.api_key.length > 0 ? form.api_key : null,
      from_number: sms.from_number ?? '',
      is_active: next,
    };
    await update.mutateAsync(body);
  }

  function extractSaveError(err: unknown): string {
    const message = err instanceof Error ? err.message : '';
    if (message.includes('password_required_for_new_config')) {
      return 'An API key is required when configuring SMS for the first time.';
    }
    return 'Save failed. Check the values and retry.';
  }

  async function handleTest() {
    setTestMessage(null);
    if (!testNumber) {
      setTestMessage({ kind: 'error', text: 'Enter a phone number.' });
      return;
    }
    try {
      const result = await test.mutateAsync({
        test_number: testNumber,
        provider: form.provider,
      });
      if (result.success) {
        setTestMessage({
          kind: 'success',
          text: `Test SMS sent to ${testNumber}.`,
        });
      } else {
        setTestMessage({
          kind: 'error',
          text: result.message || 'Test failed.',
        });
      }
    } catch {
      setTestMessage({
        kind: 'error',
        text: 'Could not run the test. Try again.',
      });
    }
  }

  return (
    <Card title="SMS Provider">
      <p className={styles.description}>
        Configure the SMS provider used for booking reminders, cancellation
        confirmations, and other customer-facing texts.
      </p>

      {query.isLoading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : (
        <>
          <div className={styles.statusRow}>
            <span
              className={notConfigured ? styles.statusBad : styles.statusOk}
              data-testid="sms-status"
            >
              {notConfigured ? 'Not configured' : 'Configured ✓'}
            </span>
            {sms && !notConfigured ? (
              <label className={styles.activeToggle}>
                <input
                  type="checkbox"
                  checked={sms.is_active}
                  onChange={(e) => void handleToggleActive(e.target.checked)}
                  aria-label="SMS provider active"
                />
                <span>{sms.is_active ? 'Active' : 'Inactive'}</span>
              </label>
            ) : null}
          </div>

          <fieldset className={styles.providerGroup}>
            <legend className={styles.providerLegend}>Provider</legend>
            <div className={styles.providerOptions}>
              <label className={styles.providerOption}>
                <input
                  type="radio"
                  name="sms-provider"
                  value="clicksend"
                  checked={form.provider === 'clicksend'}
                  onChange={() =>
                    setForm({ ...form, provider: 'clicksend' })
                  }
                />
                <span className={styles.providerName}>ClickSend</span>
                <span className={styles.providerNote}>Live</span>
              </label>
              <label
                className={`${styles.providerOption} ${styles.providerDisabled}`}
              >
                <input
                  type="radio"
                  name="sms-provider"
                  value="twilio"
                  disabled
                  aria-disabled="true"
                />
                <span className={styles.providerName}>Twilio</span>
                <span className={styles.providerNoteMuted}>Coming Soon</span>
              </label>
              <label
                className={`${styles.providerOption} ${styles.providerDisabled}`}
              >
                <input
                  type="radio"
                  name="sms-provider"
                  value="vonage"
                  disabled
                  aria-disabled="true"
                />
                <span className={styles.providerName}>Vonage</span>
                <span className={styles.providerNoteMuted}>Coming Soon</span>
              </label>
            </div>
          </fieldset>

          {!editing ? (
            <div className={styles.viewBlock}>
              {sms && !notConfigured ? (
                <dl className={styles.summary}>
                  <div className={styles.summaryRow}>
                    <dt>Provider</dt>
                    <dd>{sms.provider}</dd>
                  </div>
                  <div className={styles.summaryRow}>
                    <dt>Username</dt>
                    <dd>{sms.api_username ?? '—'}</dd>
                  </div>
                  <div className={styles.summaryRow}>
                    <dt>From number</dt>
                    <dd>{sms.from_number ?? '—'}</dd>
                  </div>
                </dl>
              ) : null}

              <div className={styles.actions}>
                <Button
                  variant="secondary"
                  icon={Pencil}
                  size="sm"
                  onClick={startEdit}
                >
                  {notConfigured ? 'Configure' : 'Edit'}
                </Button>
              </div>

              {sms && !notConfigured ? (
                <div className={styles.testBlock}>
                  {!testOpen ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setTestOpen(true)}
                    >
                      Test SMS
                    </Button>
                  ) : (
                    <div className={styles.testForm}>
                      <Input
                        label="Send test to"
                        variant="tel"
                        value={testNumber}
                        onChange={(e) => setTestNumber(e.target.value)}
                        placeholder="+447700900000"
                        helper="E.164 format, e.g. +447700900000"
                      />
                      <div className={styles.testButtons}>
                        <Button
                          variant="primary"
                          size="sm"
                          loading={test.isPending}
                          onClick={() => void handleTest()}
                        >
                          Send Test
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setTestOpen(false);
                            setTestMessage(null);
                            setTestNumber('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                      {testMessage ? (
                        <p
                          className={
                            testMessage.kind === 'success'
                              ? styles.testSuccess
                              : styles.testError
                          }
                          role={
                            testMessage.kind === 'error' ? 'alert' : 'status'
                          }
                        >
                          {testMessage.kind === 'success' ? '✓ ' : '✗ '}
                          {testMessage.text}
                        </p>
                      ) : null}
                    </div>
                  )}
                  {sms.last_tested_at ? (
                    <p className={styles.lastTested}>
                      Last tested: {relativeTime(sms.last_tested_at)} —{' '}
                      {sms.last_test_result === 'success'
                        ? '✓ success'
                        : '✗ failed'}
                      {sms.last_test_message ? ` — ${sms.last_test_message}` : ''}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            <form
              className={styles.form}
              onSubmit={(e) => {
                e.preventDefault();
                void handleSave();
              }}
            >
              <div className={styles.row}>
                <Input
                  label="API Username"
                  required
                  value={form.api_username}
                  onChange={(e) =>
                    setForm({ ...form, api_username: e.target.value })
                  }
                />
                <Input
                  label="API Key"
                  variant="password"
                  placeholder="Leave blank to keep existing"
                  value={form.api_key}
                  onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                  helper="Blank = keep the previously-saved key. Required only when configuring for the first time."
                />
              </div>
              <div className={styles.row}>
                <Input
                  label="From Number"
                  variant="tel"
                  required
                  value={form.from_number}
                  onChange={(e) =>
                    setForm({ ...form, from_number: e.target.value })
                  }
                  helper="E.164 format, e.g. +447700900000"
                />
              </div>
              {saveError ? (
                <p className={styles.saveError} role="alert">
                  {saveError}
                </p>
              ) : null}
              <div className={styles.formButtons}>
                <Button
                  type="submit"
                  variant="primary"
                  loading={update.isPending}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={cancelEdit}
                  disabled={update.isPending}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </>
      )}
    </Card>
  );
}
